/**
 * Enables arrow key navigation between anchor elements inside a list container.
 *
 * - `ArrowDown` moves focus to the next link in the list.
 * - `ArrowUp` moves focus to the previous link, or calls `onEscapeTop` when
 *   already at the first item (e.g. to return focus to a search input).
 *
 * @param listRef - A template ref pointing to the container element whose `<a>` elements should be navigable.
 * @param onEscapeTop - Optional callback invoked when the user presses ArrowUp on the first item.
 *
 * @returns
 * - `onKeydown` — attach to `@keydown` on each focusable link inside the list.
 * - `focusFirst` — programmatically focuses the first link; useful for entering the list from an input.
 */
export function useListKeyboardNavigation(listRef: Ref<HTMLElement | null>, onEscapeTop?: () => void) {
  function onKeydown(event: KeyboardEvent) {
    const links = Array.from(listRef.value?.querySelectorAll<HTMLAnchorElement>('a') ?? [])
    const currentIndex = links.findIndex(link => link === document.activeElement)

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      links[Math.min(currentIndex + 1, links.length - 1)]?.focus()
    }
    else if (event.key === 'ArrowUp') {
      event.preventDefault()
      if (currentIndex <= 0) {
        onEscapeTop?.()
      }
      else {
        links[currentIndex - 1]?.focus()
      }
    }
  }

  function focusFirst() {
    listRef.value?.querySelector<HTMLAnchorElement>('a')?.focus()
  }

  return { onKeydown, focusFirst }
}
